// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type ReactNode, useCallback, useMemo, useContext } from 'react';

import type { Facility } from '@evolvemep/api-ts';
import { Card, Flex, Stack, Text, Title, LoadingOverlay } from '@mantine/core';

import type { GlobalNavigationItemLabel } from 'app/Layout/NavBar/globalNavigationMenu';
import { FacilitySelect } from 'components/Mantine/GlobalFilters/FacilitySelect';
import { useUpdateGlobalFilters } from 'components/Mantine/GlobalFilters/useGlobalFilters';
import type { PageTitle } from 'components/Mantine/Navigation/types';
import { hasLengthExactly } from 'helpers/helper-types';
import { isNil } from 'helpers/isNotNil';
import { useGeneralContext } from 'helpers/useGeneralContext';

type FacilityRequiredType = {
  selectedFacility: Facility;
  setSelectedFacility: (facility: Facility | null) => void;
};

const FacilityRequiredContext = React.createContext<FacilityRequiredType | undefined>(undefined);

/**
 * Used when a Facility *must* be selected
 * in order to render the child page
 */
export const FacilityRequired = ({
  title,
  loading,
  children,
}: {
  title: PageTitle | GlobalNavigationItemLabel;
  loading?: boolean;
  children: ReactNode | ((facility: Facility) => ReactNode);
}) => {
  const {
    globalFilters: { facilities },
    updateGlobalFilters,
  } = useUpdateGlobalFilters();
  const selectedFacility = useMemo(() => (hasLengthExactly(facilities, 1) ? facilities[0] : null), [facilities]);

  const onFacilitySelected = useCallback(
    (facility: Facility | null) =>
      updateGlobalFilters({
        facilities: facility ? [facility] : [],
      }),
    [updateGlobalFilters],
  );

  const value = useMemo(
    () => ({ selectedFacility, setSelectedFacility: onFacilitySelected }),
    [onFacilitySelected, selectedFacility],
  );

  if (isNil(value.selectedFacility)) {
    return (
      <Flex flex={1} align="center" justify="center">
        <Card shadow="md" withBorder>
          <Stack mb="sm">
            <Title order={4} fw={500} mt="xs" c="gray.7">
              Select a facility to continue
            </Title>
            <Text>
              <b>{title}</b> requires you to first select a facility.
            </Text>
            <FacilitySelect onChange={onFacilitySelected} value={selectedFacility} autoFocus />
          </Stack>
          <LoadingOverlay visible={loading} />
        </Card>
      </Flex>
    );
  }

  return (
    <FacilityRequiredContext value={value as FacilityRequiredType}>
      {typeof children === 'function' ? children(value.selectedFacility) : children}
    </FacilityRequiredContext>
  );
};

export const useFacilityRequired = () => useGeneralContext(FacilityRequiredContext, 'FacilityRequired');
export const useFacilityOptional = () => useContext(FacilityRequiredContext);
